import React, { useState, useEffect } from "react";
import { FiBell } from "react-icons/fi";
import NotificationPopup from "../pages/Notificationpopup";
import '../pages/CropMarketPage.css';
import './Header.css';

const Header = ({ isDistrictView = false, isStateView = false, districtName = '', stateName = '' }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isMarqueePaused, setIsMarqueePaused] = useState(false);
  const [userRole, setUserRole] = useState('farmer');
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');

  // Get user data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role || 'farmer');
        setSelectedState(user.selectedState || '');
        setSelectedDistrict(user.selectedDistrict || '');
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/disaster-notifications`);
        const json = await res.json();
        const states = json.data.states || [];

        // Filter notifications for district or state view
        let filteredStates = states;
        if ((isDistrictView || isStateView) && stateName) {
          filteredStates = states.filter(state => 
            state.state.toLowerCase().includes(stateName.toLowerCase())
          );
        }

        setHasNewNotifications(filteredStates.length > 0);

        const scoredStates = filteredStates.map((state) => {
          // Use the totalRiskScore from the enhanced API if available
          const riskScore = state.totalRiskScore || 
            (state.floodRisk === "High" ? 2 : state.floodRisk === "Medium" ? 1 : 0) +
            (state.droughtRisk === "High" ? 2 : state.droughtRisk === "Medium" ? 1 : 0);
          
          return { ...state, riskScore };
        });

        const topRiskStates = scoredStates
          .sort((a, b) => b.riskScore - a.riskScore)
          .slice(0, 5);

        setNotifications(topRiskStates);
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 1000 * 60 * 60);
    return () => clearInterval(interval);
  }, [isDistrictView, isStateView, stateName]);

  const marqueeContent = notifications.length > 0
    ? notifications
        .map((state) => {
          let conditionText = '';
          
          // Priority 1: Check for extreme/high severity alerts first (ANY type)
          if (state.weatherAlerts && state.weatherAlerts.length > 0) {
            const extremeAlert = state.weatherAlerts.find(alert => alert.severity === 'extreme');
            const highAlert = state.weatherAlerts.find(alert => alert.severity === 'high');
            
            if (extremeAlert) {
              // Use full description, don't split
              conditionText = extremeAlert.description;
            } else if (highAlert) {
              // Use full description, don't split
              conditionText = highAlert.description;
            }
          }
          
          // Priority 2: Check flood risk (rainfall)
          if (!conditionText) {
            if (state.floodRisk === 'High') {
              conditionText = 'Heavy Rainfall/Flood Risk';
            } else if (state.floodRisk === 'Medium') {
              conditionText = 'Moderate Rainfall Risk';
            }
          }
          
          // Priority 3: Check drought with heat
          if (!conditionText && state.droughtRisk === 'High') {
            const temp = state.riskFactors?.currentTemp || 0;
            if (temp >= 35) {
              conditionText = 'Severe Drought & Heat';
            } else if (temp >= 30) {
              conditionText = 'Drought Conditions';
            }
          }
          
          // Priority 4: Medium drought with heat
          if (!conditionText && state.droughtRisk === 'Medium') {
            const temp = state.riskFactors?.currentTemp || 0;
            if (temp >= 35) {
              conditionText = 'Drought Risk';
            }
          }
          
          // Priority 5: Check for moderate severity alerts if nothing else
          if (!conditionText && state.weatherAlerts && state.weatherAlerts.length > 0) {
            const moderateAlert = state.weatherAlerts.find(alert => alert.severity === 'moderate');
            if (moderateAlert) {
              // Use full description, don't split
              conditionText = moderateAlert.description;
            }
          }
          
          // Priority 6: Show overall risk if present
          if (!conditionText && state.overallRisk === 'High') {
            conditionText = 'High Weather Risk';
          }
          
          // Return state with condition, or null if no significant condition
          return conditionText ? `${state.state}: ${conditionText}` : null;
        })
        .filter(item => item !== null) // Remove null entries
        .join(" • ")
    : ((isDistrictView || isStateView)
        ? `No weather alerts currently active for ${isDistrictView ? `${districtName}, ` : ''}${stateName}` 
        : "No state as of now is high alert due to the rainfall");
  
  // If all states filtered out, show appropriate message
  const finalMarqueeContent = marqueeContent && marqueeContent.trim() && !marqueeContent.includes('null')
    ? marqueeContent
    : ((isDistrictView || isStateView)
        ? `No severe weather alerts currently active for ${isDistrictView ? `${districtName}, ` : ''}${stateName}` 
        : "No states currently under severe weather alert");

  // Get header content based on user role
  const getHeaderContent = () => {
    switch (userRole) {
      case 'state':
        return {
          title: `🏛️ ${selectedState} State Administration Dashboard`,
          subtitle: `State-level <strong>agricultural monitoring</strong> and <strong>policy management</strong> for ${selectedState}`
        };
      case 'district':
        return {
          title: `🏢 ${selectedDistrict} District Administration Dashboard`,
          subtitle: `District-level <strong>agricultural coordination</strong> and <strong>resource management</strong> for ${selectedDistrict}, ${selectedState}`
        };
      default:
        return {
          title: '🌾 Advanced AI Agriculture Dashboard',
          subtitle: 'Monitor <strong>weather data</strong>, assess <strong>disaster risks</strong>, and enable <strong>all-India agricultural intelligence</strong>'
        };
    }
  };

  const headerContent = getHeaderContent();

  const handleMarqueeClick = () => {
    setShowNotifications(true);
    setHasNewNotifications(false);
  };

  return (
    <div className="header-section">
      <div
        className="header-bell-btn"
        onClick={() => {
          setShowNotifications(true);
          setHasNewNotifications(false);
        }}
      >
        <FiBell />
        {hasNewNotifications && (
          <span className="header-bell-dot" />
        )}
      </div>
      
      <h1 className="header-title">
        {headerContent.title}
      </h1>
      <p className="header-subtitle">
        <span dangerouslySetInnerHTML={{ __html: headerContent.subtitle }} />
      </p>

      <div
        className="header-marquee-wrapper"
        onClick={handleMarqueeClick}
        onMouseEnter={() => setIsMarqueePaused(true)}
        onMouseLeave={() => setIsMarqueePaused(false)}
      >
        <div
          className="header-marquee-text"
          style={{ animationPlayState: isMarqueePaused ? "paused" : "running" }}
        >
          {finalMarqueeContent}
        </div>
      </div>

      {showNotifications && (
        <NotificationPopup
          onClose={() => {
            setShowNotifications(false);
            setHasNewNotifications(false);
          }}
        />
      )}
    </div>
  );
};

export default Header;